// The persistent result snapshot of "Areal og volumen": the part of a finished measurement that is saved with the object and restored
// without a new ROI load or computation.
//
// Canonical state of the object = the polygon (feature.geometry). This snapshot is the stored RESULT of one specific polygon and
// algorithm version, not a competing source of truth: it carries the input fingerprint and the algorithm version it was computed for,
// so a restored object can tell whether the stored numbers still describe the polygon it shows (see snapshotMatchesInput()).
//
// Stored here (feature.domainData.volume, see volumeTool.js): the professional values, the reference plane and which classes were left
// out of this result, nothing else.
// Never stored: diagnostics and timings, ROI statistics, the surface mesh and QA visual, worker/render objects, UI state
// (qaVisibility, the runtime `stale` flag). The reconstructed surface is runtime-only in this stage; a later mesh reference is a new
// sibling field of `result` (e.g. `mesh`), added when file storage exists - nothing here depends on it.

export const VOLUME_ALGORITHM_VERSION = 1;

// Stable identification of the polygon a result was computed for: its vertices (open ring, in order) at micrometre resolution, hashed.
// Both the world points the computation used and the stored geometry go through the same function, so a closed ring (first vertex
// repeated) and an open one give the same fingerprint.
export function volumeInputFingerprint(points){
	const ring = points.map((p) => [p.x !== undefined ? p.x : p[0], p.y !== undefined ? p.y : p[1], p.z !== undefined ? p.z : p[2]]);
	if(ring.length > 1){
		const a = ring[0], b = ring[ring.length - 1];
		if(a[0] === b[0] && a[1] === b[1] && a[2] === b[2]) ring.pop();
	}
	return hash53(ring.map((p) => p.map((v) => v.toFixed(6)).join(",")).join(";"));
}

// Fingerprint of a stored Polygon geometry (outer ring).
export function polygonGeometryFingerprint(coordinates){
	return volumeInputFingerprint((coordinates && coordinates[0]) || []);
}

// cyrb53: a small, fast, non-cryptographic 53-bit string hash (change detection only, no security purpose).
function hash53(text){
	let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
	for(let i = 0; i < text.length; i++){
		const ch = text.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
	return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

// The stored snapshot for a finished computation. `points` = the polygon vertices the computation actually used.
export function buildVolumeSnapshot(result, points){
	return {
		algorithmVersion: VOLUME_ALGORITHM_VERSION,
		inputFingerprint: volumeInputFingerprint(points),
		result: {
			referenceArea: result.referenceArea,
			positive: result.positive,
			negative: result.negative,
			coverageRatio: result.coverageRatio,
			noObservationsInsidePolygon: !!result.noObservationsInsidePolygon,
			plane: JSON.parse(JSON.stringify(result.plane)),
			excludedClasses: result.excludedClasses || [],
		},
	};
}

export function isVolumeSnapshot(snapshot){
	const r = snapshot && snapshot.result;
	return !!r && Number.isFinite(r.referenceArea) && Number.isFinite(r.positive) && Number.isFinite(r.negative)
		&& !!r.plane && !!r.plane.origin && typeof snapshot.inputFingerprint === "string";
}

// True when the stored numbers describe the given polygon and were produced by the current algorithm version.
export function snapshotMatchesInput(snapshot, polygonCoordinates){
	return isVolumeSnapshot(snapshot)
		&& snapshot.algorithmVersion === VOLUME_ALGORITHM_VERSION
		&& snapshot.inputFingerprint === polygonGeometryFingerprint(polygonCoordinates);
}

// The runtime `volumeResult` of a restored object: the stored values only (a fresh copy). It has no `timings`, which is how the panels
// tell a restored result (no diagnostics) from a freshly computed one.
export function volumeResultFromSnapshot(snapshot){
	return JSON.parse(JSON.stringify(snapshot.result));
}
